/**
 * Posturax Essential Boost Component
 * Maneja el countdown timer y efectos de urgencia
 */

class PosturaxCountdown extends HTMLElement {
  constructor() {
    super();
    
    this.endDate = this.dataset.endDate;
    this.timezone = this.dataset.timezone || 'America/Santiago';
    this.format = this.dataset.format || 'days,hours,minutes,seconds';
    
    this.countdownInterval = null;
    this.isExpired = false;
    
    this.initElements();
    this.init();
  }

  initElements() {
    this.daysElement = this.querySelector('[data-countdown-days]');
    this.hoursElement = this.querySelector('[data-countdown-hours]');
    this.minutesElement = this.querySelector('[data-countdown-minutes]');
    this.secondsElement = this.querySelector('[data-countdown-seconds]');
    
    this.daysNumber = this.daysElement?.querySelector('.posturax-essential-boost__countdown-number');
    this.hoursNumber = this.hoursElement?.querySelector('.posturax-essential-boost__countdown-number');
    this.minutesNumber = this.minutesElement?.querySelector('.posturax-essential-boost__countdown-number');
    this.secondsNumber = this.secondsElement?.querySelector('.posturax-essential-boost__countdown-number');
  }

  init() {
    if (!this.endDate) {
      console.warn('PosturaxCountdown: No end date provided');
      return;
    }
    
    this.startCountdown();
    this.bindEvents();
  }

  bindEvents() {
    // Pause countdown when page is hidden to save resources
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseCountdown();
      } else {
        this.resumeCountdown();
      }
    });
    
    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  startCountdown() {
    this.updateCountdown();
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  pauseCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  resumeCountdown() {
    if (!this.countdownInterval && !this.isExpired) {
      this.startCountdown();
    }
  }

  updateCountdown() {
    const now = new Date();
    const endTime = this.parseEndDate();
    
    if (!endTime) {
      this.showError();
      return;
    }
    
    const timeLeft = endTime.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      this.handleExpired();
      return;
    }
    
    const timeComponents = this.calculateTimeComponents(timeLeft);
    this.displayTime(timeComponents);
    this.updateUrgency(timeComponents);
  }

  parseEndDate() {
    try {
      // Parse the end date string (format: YYYY-MM-DD HH:MM)
      const [datePart, timePart] = this.endDate.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      // Create date in the specified timezone
      const endDate = new Date(year, month - 1, day, hours, minutes, 0);
      
      // Adjust for timezone if needed
      if (this.timezone !== 'UTC') {
        const timezoneOffset = this.getTimezoneOffset(this.timezone);
        endDate.setTime(endDate.getTime() - timezoneOffset);
      }
      
      return endDate;
    } catch (error) {
      console.error('Error parsing end date:', error);
      return null;
    }
  }

  getTimezoneOffset(timezone) {
    // Simple timezone offset calculation
    // In a real implementation, you might want to use a library like moment-timezone
    const timezoneOffsets = {
      'America/Santiago': -3 * 60 * 60 * 1000, // UTC-3
      'America/New_York': -5 * 60 * 60 * 1000, // UTC-5
      'America/Los_Angeles': -8 * 60 * 60 * 1000, // UTC-8
      'Europe/Madrid': 1 * 60 * 60 * 1000, // UTC+1
      'UTC': 0
    };
    
    return timezoneOffsets[timezone] || 0;
  }

  calculateTimeComponents(timeLeft) {
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds };
  }

  displayTime(components) {
    if (this.daysNumber) {
      this.daysNumber.textContent = this.padZero(components.days);
    }
    
    if (this.hoursNumber) {
      this.hoursNumber.textContent = this.padZero(components.hours);
    }
    
    if (this.minutesNumber) {
      this.minutesNumber.textContent = this.padZero(components.minutes);
    }
    
    if (this.secondsNumber) {
      this.secondsNumber.textContent = this.padZero(components.seconds);
    }
  }

  updateUrgency(components) {
    const { days, hours, minutes } = components;
    const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
    
    // Add urgency effects based on time remaining
    if (totalMinutes <= 60) {
      // Less than 1 hour - high urgency
      this.addUrgencyClass('high');
    } else if (totalMinutes <= 24 * 60) {
      // Less than 1 day - medium urgency
      this.addUrgencyClass('medium');
    } else {
      // More than 1 day - low urgency
      this.removeUrgencyClass();
    }
  }

  addUrgencyClass(level) {
    this.classList.add(`urgency-${level}`);
    
    // Add pulsing animation to seconds
    if (this.secondsElement) {
      this.secondsElement.classList.add('is-urgent');
    }
  }

  removeUrgencyClass() {
    this.classList.remove('urgency-high', 'urgency-medium');
    
    if (this.secondsElement) {
      this.secondsElement.classList.remove('is-urgent');
    }
  }

  handleExpired() {
    this.isExpired = true;
    this.pauseCountdown();
    
    // Show expired state
    this.showExpired();
    
    // Dispatch event for other components
    this.dispatchEvent(new CustomEvent('countdown:expired', {
      detail: { endDate: this.endDate }
    }));
  }

  showExpired() {
    // Update display to show expired state
    if (this.daysNumber) this.daysNumber.textContent = '00';
    if (this.hoursNumber) this.hoursNumber.textContent = '00';
    if (this.minutesNumber) this.minutesNumber.textContent = '00';
    if (this.secondsNumber) this.secondsNumber.textContent = '00';
    
    // Add expired styling
    this.classList.add('countdown-expired');
    
    // Update message if exists
    const messageElement = this.querySelector('.posturax-essential-boost__countdown-message');
    if (messageElement) {
      messageElement.textContent = '¡La oferta ha expirado!';
    }
  }

  showError() {
    console.error('PosturaxCountdown: Invalid end date format');
    
    // Show error state
    if (this.daysNumber) this.daysNumber.textContent = '--';
    if (this.hoursNumber) this.hoursNumber.textContent = '--';
    if (this.minutesNumber) this.minutesNumber.textContent = '--';
    if (this.secondsNumber) this.secondsNumber.textContent = '--';
    
    this.classList.add('countdown-error');
  }

  padZero(num) {
    return num.toString().padStart(2, '0');
  }

  cleanup() {
    this.pauseCountdown();
  }

  // Public API
  getTimeRemaining() {
    if (this.isExpired) return null;
    
    const now = new Date();
    const endTime = this.parseEndDate();
    
    if (!endTime) return null;
    
    const timeLeft = endTime.getTime() - now.getTime();
    return timeLeft > 0 ? timeLeft : 0;
  }

  isUrgent() {
    const timeLeft = this.getTimeRemaining();
    if (!timeLeft) return false;
    
    const totalMinutes = timeLeft / (1000 * 60);
    return totalMinutes <= 60; // Less than 1 hour
  }

  // Static method to create countdown programmatically
  static create(endDate, timezone = 'America/Santiago') {
    const countdown = document.createElement('posturax-countdown');
    countdown.dataset.endDate = endDate;
    countdown.dataset.timezone = timezone;
    return countdown;
  }
}

// Register the custom element
if (!customElements.get('posturax-countdown')) {
  customElements.define('posturax-countdown', PosturaxCountdown);
}

// Global functions for external access
window.PosturaxCountdown = {
  getCountdown() {
    return document.querySelector('posturax-countdown');
  },
  
  getTimeRemaining() {
    const countdown = this.getCountdown();
    return countdown?.getTimeRemaining();
  },
  
  isUrgent() {
    const countdown = this.getCountdown();
    return countdown?.isUrgent() || false;
  },
  
  create(endDate, timezone) {
    return PosturaxCountdown.create(endDate, timezone);
  }
};

// Auto-initialize countdown effects
document.addEventListener('DOMContentLoaded', () => {
  const countdown = document.querySelector('posturax-countdown');
  
  if (countdown) {
    // Add entrance animation
    countdown.style.opacity = '0';
    countdown.style.transform = 'translateY(20px)';
    
    setTimeout(() => {
      countdown.style.transition = 'all 0.6s ease';
      countdown.style.opacity = '1';
      countdown.style.transform = 'translateY(0)';
    }, 100);
    
    // Listen for countdown events
    countdown.addEventListener('countdown:expired', () => {
      console.log('Countdown expired!');
      
      // Could trigger other actions here
      // e.g., hide certain elements, show alternative content, etc.
    });
  }
});

// Analytics integration (optional)
document.addEventListener('countdown:expired', (event) => {
  // Track countdown expiration with analytics
  if (typeof gtag !== 'undefined') {
    gtag('event', 'countdown_expired', {
      'event_category': 'engagement',
      'event_label': 'essential_boost'
    });
  }
  
  if (typeof fbq !== 'undefined') {
    fbq('trackCustom', 'CountdownExpired', {
      section: 'essential_boost'
    });
  }
});

export default PosturaxCountdown;
